/**
 * Iter-138 — Funnel event ingestor (S11: Funnel Attribution).
 *
 * Lightweight client-side helper that fires server-side funnel events
 * (via POST /api/funnel/event) so the admin cluster-attribution
 * dashboard has first-party numbers — independent of PostHog/GA4.
 *
 * Four event types (constrained server-side):
 *   - cluster_impression       — fired once per article view per session
 *   - calc_strip_click          — fired on iter-136 calc card click
 *   - offer_footer_click        — fired on iter-137 monetization CTA click
 *   - modal_submit_attributed   — fired post-modal-success, with cluster
 *
 * Impressions are sessionStorage-deduped per `(cluster, slug)` so a
 * single visitor reading one article doesn't pad the impression count.
 *
 * All calls are fire-and-forget: failure (offline, blocked, network
 * error) silently swallows so they never block UI.
 */
import { api } from "./api";

const DEDUPE_KEY = "carsavr.funnelImpressionDedupe";

const _readSessionSet = () => {
  if (typeof window === "undefined") return new Set();
  try {
    const raw = window.sessionStorage.getItem(DEDUPE_KEY);
    return new Set(raw ? JSON.parse(raw) : []);
  } catch {
    return new Set();
  }
};

const _writeSessionSet = (set) => {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(DEDUPE_KEY, JSON.stringify([...set]));
  } catch {
    /* sessionStorage full / blocked — non-fatal */
  }
};

/**
 * Record a funnel event server-side. Returns nothing (fire-and-forget).
 *
 * @param {string} eventType  — one of the 4 valid event types
 * @param {string} cluster    — cluster key (from /api/clusters/manifest)
 * @param {string} slug       — current article slug
 * @param {object} meta       — optional metadata (calc_slug, modal_category, etc.)
 */
export function recordFunnelEvent(eventType, cluster, slug, meta = null) {
  if (!eventType || !cluster || !slug) return;
  // Dedupe impressions per (cluster, slug) per session.
  if (eventType === "cluster_impression") {
    const key = `${cluster}::${slug}`;
    const seen = _readSessionSet();
    if (seen.has(key)) return;
    seen.add(key);
    _writeSessionSet(seen);
  }
  try {
    api
      .post("/funnel/event", {
        event_type: eventType,
        cluster,
        slug,
        meta: meta || undefined,
      })
      .catch(() => {});
  } catch {
    /* swallow */
  }
}
